import { format, parseISO } from 'date-fns';

import { formatTagLabel } from '@/utils/format-utils';
import type { ComparisonPeriod, PeriodSummary } from '@/types/comparison';

interface DeltaProps {
  value: number;
  label: string;
}

function Delta({ value, label }: DeltaProps) {
  if (value === 0) return null;
  const arrow = value > 0 ? '↑' : '↓';
  const abs = Math.abs(value);
  return (
    <span className="text-body-xs text-[var(--color-text-muted)]" data-testid="delta-indicator">
      {' '}
      <span className="text-[var(--color-accent)]">{arrow}{abs}</span>
      {' '}{label}
    </span>
  );
}

function formatRangeHeading(period: ComparisonPeriod): string {
  const start = parseISO(period.range.start);
  const end = parseISO(period.range.end);
  if (format(start, 'MMM yyyy') === format(end, 'MMM yyyy')) {
    return `${format(start, 'MMM d')}–${format(end, 'd, yyyy')}`;
  }
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
}

interface SummaryCardProps {
  label: string;
  period: ComparisonPeriod;
  other: PeriodSummary;
  testId: string;
}

function SummaryCard({ label, period, other, testId }: SummaryCardProps) {
  const { summary } = period;

  const entryDelta = summary.totalEntries - other.totalEntries;
  const obsDelta = summary.totalObservations - other.totalObservations;

  const avgTempDisplay =
    summary.avgTemp !== null ? `${summary.avgTemp}°F` : '—';
  const tempDelta =
    summary.avgTemp !== null && other.avgTemp !== null
      ? Math.round((summary.avgTemp - other.avgTemp) * 10) / 10
      : null;

  const avgPressureDisplay =
    summary.avgPressure !== null ? `${summary.avgPressure} inHg` : '—';

  return (
    <div
      className="flex-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-3"
      data-testid={testId}
    >
      {/* Period header */}
      <div>
        <p className="field-label">
          {label}
        </p>
        <p className="text-body-sm text-[var(--color-text-secondary)] mt-0.5">
          {formatRangeHeading(period)}
        </p>
      </div>

      {summary.totalEntries === 0 ? (
        <p className="text-body-sm text-[var(--color-text-muted)]" data-testid={`${testId}-empty`}>
          No scouting entries in this period
        </p>
      ) : (
        <>
          {/* Entry count */}
          <div>
            <p className="text-display-sm font-display text-[var(--color-text-display)]">
              {summary.totalEntries}
              <span className="text-body-sm font-normal text-[var(--color-text-muted)] ml-1">
                entries
              </span>
              <Delta value={entryDelta} label="entries" />
            </p>
          </div>

          {/* Animal observations */}
          {summary.totalObservations > 0 && (
            <div>
              <p className="field-label mb-1">
                Observations
              </p>
              <p className="text-body-sm text-[var(--color-text-primary)]">
                {summary.totalObservations} total
                <Delta value={obsDelta} label="observations" />
              </p>
              {(summary.buckCount > 0 || summary.doeCount > 0 || summary.fawnCount > 0) && (
                <p className="text-body-xs text-[var(--color-text-secondary)] mt-0.5">
                  {[
                    summary.buckCount > 0 && `${summary.buckCount} buck`,
                    summary.doeCount > 0 && `${summary.doeCount} doe`,
                    summary.fawnCount > 0 && `${summary.fawnCount} fawn`,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Weather */}
          <div>
            <p className="field-label mb-1">
              Weather
            </p>
            <p className="text-body-sm text-[var(--color-text-primary)]">
              Avg temp: {avgTempDisplay}
              {tempDelta !== null && tempDelta !== 0 && (
                <Delta value={tempDelta} label="°F" />
              )}
            </p>
            {summary.avgPressure !== null && (
              <p className="text-body-xs text-[var(--color-text-secondary)] mt-0.5">
                Avg pressure: {avgPressureDisplay}
              </p>
            )}
          </div>

          {/* Top tags */}
          {summary.topTags.length > 0 && (
            <div>
              <p className="field-label mb-1.5">
                Top Tags
              </p>
              <div className="flex flex-wrap gap-1">
                {summary.topTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-body-xs border border-[var(--color-border-subtle)] text-[var(--color-text-secondary)]"
                  >
                    {formatTagLabel(tag)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Photo count */}
          {summary.photoCount > 0 && (
            <p className="text-body-xs text-[var(--color-text-muted)]">
              {summary.photoCount} photo{summary.photoCount !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}
    </div>
  );
}

interface ComparisonSummaryProps {
  periodA: ComparisonPeriod;
  periodB: ComparisonPeriod;
}

export function ComparisonSummary({ periodA, periodB }: ComparisonSummaryProps) {
  return (
    <div className="flex flex-col sm:flex-row gap-3" data-testid="comparison-summary">
      <SummaryCard
        label="Period A"
        period={periodA}
        other={periodB.summary}
        testId="summary-period-a"
      />
      <SummaryCard
        label="Period B"
        period={periodB}
        other={periodA.summary}
        testId="summary-period-b"
      />
    </div>
  );
}
